// Wishlist ♡ — the list of saved product handles lives in localStorage ("wishlist")
// and is mirrored to the Gobi Loyalty app proxy (/apps/loyalty/wishlist), which
// emails the shopper through Shopify Flow + Messaging when a saved product is back
// in stock or on sale. Signed-in shoppers sync through their account (the proxy
// knows them from its signature); a guest is asked once for an email by the
// <wishlist-alerts> prompt (snippets/wishlist-alerts.liquid) and their saves are
// sent with it. Marketing consent goes through Shopify's native customer form,
// like the back-in-stock form (product-notify-me.js), because Messaging only
// emails subscribed customers.
(function () {
  if (window.WishlistToggle) return;

  const STORAGE_KEY = 'wishlist';
  const EMAIL_KEY = 'wishlist_email'; // guest email given to the prompt
  const DISMISSED_KEY = 'wishlist_alerts_dismissed'; // when the guest closed the prompt
  const OWNER_KEY = 'wishlist_owner'; // customer whose server list this one mirrors
  const SYNCED_KEY = 'wishlist_synced'; // sessionStorage: customer synced this session
  const DISMISS_DAYS = 30;
  const ENDPOINT = '/apps/loyalty/wishlist';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function getItem(area, key) {
    try {
      return window[area].getItem(key);
    } catch (e) {
      return null;
    }
  }

  function setItem(area, key, value) {
    try {
      if (value === null) {
        window[area].removeItem(key);
      } else {
        window[area].setItem(key, value);
      }
    } catch (e) {}
  }

  function readHandles() {
    const stored = getItem('localStorage', STORAGE_KEY);
    if (!stored) return [];
    return stored.split(',').map(h => h.trim()).filter(h => h && h !== 'undefined');
  }

  function writeHandles(handles) {
    setItem('localStorage', STORAGE_KEY, handles.length === 0 ? null : handles.join(','));
    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { handles: handles.slice() }
    }));
  }

  function syncButtons(root) {
    const handles = readHandles();
    const scope = root || document;
    scope.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const handle = btn.dataset.wishlistHandle;
      if (!handle) return;
      const active = handles.includes(handle);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      // Keep the accessible name in sync with state (localized labels come from data-* attrs).
      const label = active ? btn.dataset.wishlistLabelRemove : btn.dataset.wishlistLabelAdd;
      if (label) btn.setAttribute('aria-label', label);
    });
  }

  // ---- Server sync -------------------------------------------------------

  function alertsElement() {
    return document.querySelector('wishlist-alerts');
  }

  function customerId() {
    const el = alertsElement();
    return (el && el.dataset.customerId) || '';
  }

  function locale() {
    const el = alertsElement();
    return (el && el.dataset.locale) || document.documentElement.lang || '';
  }

  function guestEmail() {
    return getItem('localStorage', EMAIL_KEY) || '';
  }

  // Never throws: a failed sync must not break the ♡ itself.
  async function api(body) {
    try {
      const response = await fetch(ENDPOINT, {
        method: body ? 'POST' : 'GET',
        headers: body
          ? { 'Content-Type': 'application/json', Accept: 'application/json' }
          : { Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      });
      const json = await response.json().catch(() => null);
      return { ok: response.ok && Boolean(json && json.ok), json };
    } catch (e) {
      return { ok: false, json: null };
    }
  }

  function push(op, handles) {
    if (customerId()) return api({ op, handles, locale: locale() });
    const email = guestEmail();
    if (email) return api({ op, handles, email, locale: locale() });
    return null;
  }

  // Signed in: on the first visit of a session (or `force`, the wishlist page)
  // pull the account's list. The first time an account is seen on this device
  // the local list is merged into it ("sync"); after that the server copy wins,
  // so removals made on another device stick.
  let accountSync = null;
  function syncAccount(options) {
    const id = customerId();
    if (!id) return Promise.resolve();
    const force = Boolean(options && options.force);
    if (!force && getItem('sessionStorage', SYNCED_KEY) === id) return Promise.resolve();
    if (accountSync) return accountSync;

    const merge = getItem('localStorage', OWNER_KEY) !== id;
    accountSync = (merge ? api({ op: 'sync', handles: readHandles(), locale: locale() }) : api(null))
      .then(({ ok, json }) => {
        if (!ok || !json || !Array.isArray(json.handles)) return;
        setItem('localStorage', OWNER_KEY, id);
        setItem('sessionStorage', SYNCED_KEY, id);
        const local = readHandles();
        const server = json.handles;
        if (local.length !== server.length || local.some((h, i) => h !== server[i])) {
          writeHandles(server);
          syncButtons();
        }
      })
      .finally(() => {
        accountSync = null;
      });
    return accountSync;
  }

  // Fire-and-forget: Shopify's native customer form records marketing consent
  // (honouring the store's double opt-in setting). Without it the alert emails —
  // sent by Shopify Messaging — would be skipped for new addresses.
  function subscribeMarketing(email) {
    const body = new URLSearchParams();
    body.append('form_type', 'customer');
    body.append('utf8', '✓');
    body.append('contact[email]', email);
    body.append('contact[tags]', 'wishlist');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}contact`, {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        Accept: 'text/html'
      }
    }).catch(() => {});
  }

  // Guest email from the prompt or the wishlist-page banner: send every saved
  // product with it and remember it, so later saves sync without asking again.
  async function submitEmail(rawEmail) {
    const email = String(rawEmail || '').trim();
    if (!EMAIL_RE.test(email)) return { ok: false, error: 'invalid_email' };
    subscribeMarketing(email);
    const { ok, json } = await api({ op: 'sync', handles: readHandles(), email, locale: locale() });
    if (!ok) return { ok: false, error: (json && json.error) || 'server_error' };
    setItem('localStorage', EMAIL_KEY, email.toLowerCase());
    return { ok: true, email };
  }

  function maskEmail(email) {
    return String(email).replace(/^(.)[^@]*(@.*)$/, '$1•••$2');
  }

  // Fills `el` with `template`, its %EMAIL% token replaced by the masked address.
  function fillWithEmail(el, template, email) {
    const parts = String(template || '').split('%EMAIL%');
    const address = document.createElement('span');
    address.className = 'wishlist-alerts__email';
    address.textContent = maskEmail(email);
    el.textContent = parts[0] || '';
    if (parts.length > 1) el.append(address, parts.slice(1).join(''));
  }

  function promptDismissedRecently() {
    const at = Number(getItem('localStorage', DISMISSED_KEY) || 0);
    return at > 0 && Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  }

  // ---- ♡ toggle ----------------------------------------------------------

  function toggle(handle, btn) {
    const handles = readHandles();
    const idx = handles.indexOf(handle);
    const added = idx === -1;
    if (added) {
      handles.push(handle);
    } else {
      handles.splice(idx, 1);
    }
    writeHandles(handles);
    syncButtons();

    if (!customerId()) {
      // Changed while signed out: merge into the account again at next sign-in.
      setItem('localStorage', OWNER_KEY, null);
      setItem('sessionStorage', SYNCED_KEY, null);
    }

    if (push(added ? 'add' : 'remove', [handle])) return;
    const prompt = alertsElement();
    if (added && prompt && typeof prompt.open === 'function' && !promptDismissedRecently()) {
      prompt.open(btn);
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.wishlistHandle;
    if (!handle) return;
    toggle(handle, btn);
  });

  // ---- Guest prompt (<wishlist-alerts>) ----------------------------------

  if (!customElements.get('wishlist-alerts')) {
    customElements.define('wishlist-alerts', class WishlistAlerts extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('.wishlist-alerts');
        if (!this.dialog || this.form) return; // signed in: no prompt markup
        this.overlay = this.querySelector('.wishlist-alerts__overlay');
        this.titleEl = this.dialog.querySelector('.wishlist-alerts__title');
        this.form = this.dialog.querySelector('form');
        this.input = this.form.elements.email;
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.status = this.dialog.querySelector('.wishlist-alerts__status');
        this.views = this.dialog.querySelectorAll('[data-wishlist-alerts-view]');
        this.successText = this.dialog.querySelector('[data-wishlist-alerts-success]');
        this.formTitle = this.titleEl.textContent;

        this.dialog.querySelectorAll('[data-wishlist-alerts-close]').forEach(button =>
          button.addEventListener('click', () => this.close())
        );
        if (this.overlay) this.overlay.addEventListener('click', () => this.close());
        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.close();
        });
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      // Phones get a modal sheet (scrim + focus trap); wider screens a toast that
      // leaves the page usable.
      isSheet() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      open(opener) {
        if (!this.dialog || this.hasAttribute('open')) return;
        this.opener = opener || document.activeElement;
        this.done = false;
        this.showView('form');
        this.titleEl.textContent = this.formTitle;
        this.hideStatus();

        const sheet = this.isSheet();
        this.setAttribute('open', '');
        this.dialog.setAttribute('aria-modal', sheet ? 'true' : 'false');
        this.dialog.hidden = false;
        if (this.overlay) this.overlay.hidden = !sheet;
        if (sheet && typeof trapFocus === 'function') {
          trapFocus(this.dialog, this.dialog);
        } else {
          this.dialog.focus();
        }
      }

      close() {
        if (!this.hasAttribute('open')) return;
        // Closed without an email: don't ask again for a while.
        if (!this.done) setItem('localStorage', DISMISSED_KEY, String(Date.now()));
        this.removeAttribute('open');
        this.dialog.hidden = true;
        if (this.overlay) this.overlay.hidden = true;
        const opener = this.opener && this.opener.isConnected ? this.opener : null;
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(opener);
        } else if (opener) {
          opener.focus();
        }
      }

      showView(name) {
        this.views.forEach(view => {
          view.hidden = view.dataset.wishlistAlertsView !== name;
        });
      }

      showStatus(message) {
        this.status.textContent = message || '';
        this.status.hidden = false;
        this.input.setAttribute('aria-invalid', 'true');
      }

      hideStatus() {
        this.status.hidden = true;
        this.status.textContent = '';
        this.input.removeAttribute('aria-invalid');
      }

      async onSubmit(event) {
        event.preventDefault();
        // Honeypot: real shoppers never fill it — pretend it worked.
        if (this.form.elements.website && this.form.elements.website.value) {
          this.close();
          return;
        }
        const email = (this.input.value || '').trim();
        if (!EMAIL_RE.test(email)) {
          this.showStatus(this.dataset.textInvalidEmail);
          this.input.focus();
          return;
        }
        this.hideStatus();
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.disabled = true;
        const result = await submitEmail(email);
        this.submitButton.removeAttribute('aria-busy');
        this.submitButton.disabled = false;

        if (!result.ok) {
          this.showStatus(result.error === 'invalid_email' ? this.dataset.textInvalidEmail : this.dataset.textError);
          this.input.focus();
          return;
        }
        this.done = true;
        this.titleEl.textContent = this.titleEl.dataset.titleSuccess || this.formTitle;
        fillWithEmail(this.successText, this.dataset.textSuccess, result.email);
        this.showView('success');
        // Re-trap: the focusable set changed with the view.
        if (this.dialog.getAttribute('aria-modal') === 'true' && typeof trapFocus === 'function') {
          trapFocus(this.dialog, this.dialog);
        } else {
          this.dialog.focus();
        }
      }
    });
  }

  function init() {
    syncButtons();
    syncAccount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.WishlistToggle = {
    sync: syncButtons,
    read: readHandles,
    write: writeHandles,
    syncAccount,
    submitEmail,
    fillWithEmail,
    hasAlertsEmail: () => Boolean(customerId() || guestEmail()),
    isSignedIn: () => Boolean(customerId())
  };
})();
